/**
 * Users API Service
 * API calls for user assignment management
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// Token getter function - set by AuthContext
// Accepts optional forceRefresh parameter to bypass MSAL cache
let getToken: ((forceRefresh?: boolean) => Promise<string | null>) | null = null;

export function setUsersTokenGetter(getter: (forceRefresh?: boolean) => Promise<string | null>) {
  getToken = getter;
}

/**
 * User assignment record
 */
export interface UserAssignment {
  userId: string;
  email: string;
  displayName: string;
  role: string;
  assignedLocationIds: number[];
  assignedDepartmentIds?: string[];
  updatedAt?: string;
  updatedBy?: string;
}

/**
 * API response wrapper
 */
interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  count?: number;
}

/**
 * Get authorization headers
 */
async function getAuthHeaders(): Promise<Record<string, string>> {
  const token = getToken ? await getToken() : null;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

/**
 * Get current user's location assignment
 * Called on login to determine allowed locations for DeptMgr
 */
export async function getUserAssignment(): Promise<ApiResponse<UserAssignment>> {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/users/assignment`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      return { success: false, error: error.error || `HTTP ${response.status}` };
    }

    return await response.json();
  } catch (error: any) {
    console.error('Error getting user assignment:', error);
    return { success: false, error: error.message || 'Failed to get user assignment' };
  }
}

/**
 * Get all users with their assignments
 * For the Users page (Admin/Executive only)
 */
export async function getAllUsers(): Promise<ApiResponse<UserAssignment[]>> {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/users`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      return { success: false, error: error.error || `HTTP ${response.status}` };
    }

    return await response.json();
  } catch (error: any) {
    console.error('Error getting users:', error);
    return { success: false, error: error.message || 'Failed to get users' };
  }
}

/**
 * Save user location assignment
 * Admin/Executive can assign locations to users (primarily DeptMgr)
 */
export async function saveUserAssignment(
  assignment: Omit<UserAssignment, 'updatedAt' | 'updatedBy'>
): Promise<ApiResponse<UserAssignment>> {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/users/assignment`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(assignment),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      return { success: false, error: error.error || `HTTP ${response.status}` };
    }

    return await response.json();
  } catch (error: any) {
    console.error('Error saving user assignment:', error);
    return { success: false, error: error.message || 'Failed to save user assignment' };
  }
}
